import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  getOrganizationUserDetailOrganizationOrganizationIdUsersUseruuidGetOptions,
  getOrganizationUserDetailOrganizationOrganizationIdUsersUseruuidGetQueryKey,
} from '@/generated/@tanstack/react-query.gen'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import DeleteUserModal from '../_components/delete-user-modal'
import ResetPasswordModal from '../_components/reset-password-modal'
import EditUserForm from './_components/edit-user-form'
import { useState } from 'react'
import { userDetailsDTO } from './_data/user-details-dto'
import UserDetailsInformation from './_components/user-details-information'
import { UserStatus } from './_data/user-details-interface'
import ResendInvitationModal from '../_components/resend-invitation-modal'
import ActivateUserModal from '../_components/activate-user-modal'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import FullScreenLoader from '@/components/full-screen-loader'
import { useUser } from '../../user-provider'

export default function UserDetails() {
  const navigator = useNavigate()
  const [openActivateModal, setOpenActivateModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openReInvitationModal, setOpenReInvitationModal] = useState(false)
  const { id } = useParams<{ id: string }>()
  const { organizationId, id: currentUserId, availableSeats } = useUser()

  const isCurrentUser = currentUserId === id

  const [isEditing, setIsEditing] = useState(false)

  const { data, isLoading } = useQuery({
    ...getOrganizationUserDetailOrganizationOrganizationIdUsersUseruuidGetOptions(
      {
        path: { organization_id: organizationId, useruuid: id! },
      }
    ),
    enabled: Boolean(id),
    select: userDetailsDTO,
  })

  const queryClient = useQueryClient()
  const onValidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey:
        getOrganizationUserDetailOrganizationOrganizationIdUsersUseruuidGetQueryKey(
          {
            path: { organization_id: organizationId, useruuid: id! },
          }
        ),
    })
  }

  const EditButton = ({ className }: { className: string }) => {
    // if (isCurrentUser) return null
    return (
      <Button
        onClick={() => {
          setIsEditing(true)
        }}
        className={className}
        variant="window"
        size="sm"
      >
        <Pencil className="h-3 w-3" />
        Edit
      </Button>
    )
  }

  const StatusButtonAction = ({ className }: { className?: string }) => {
    if (!data) return null
    if (
      data.status === UserStatus.Active ||
      data.status === UserStatus.Deactivated
    ) {
      return (
        <div className={className}>
          <ActivateUserModal
            open={openActivateModal}
            setOpen={setOpenActivateModal}
            email={data.email}
            isActivated={data.status === UserStatus.Active}
            userId={id!}
            onStatusChanged={onValidateQuery}
            organizationId={organizationId}
          />
        </div>
      )
    }

    if (data.status === UserStatus.Invited) {
      return (
        <div className={className}>
          <ResendInvitationModal
            disabled={data.invitationValid === false && availableSeats === 0}
            open={openReInvitationModal}
            setOpen={setOpenReInvitationModal}
            userId={id!}
            email={data.email}
            organizationId={organizationId}
          />
        </div>
      )
    }

    return null
  }

  const ResetPasswordButton = ({ className }: { className?: string }) => {
    if (!data) return null
    // if (data.status !== UserStatus.Invited) return null
    return (
      <div className={className}>
        <ResetPasswordModal
          email={data.email}
          userId={id!}
          organizationId={organizationId}
        />
      </div>
    )
  }

  const DeleteButton = ({ className }: { className?: string }) => {
    if (isCurrentUser) return null
    return (
      <div className={className}>
        <DeleteUserModal
          onDeleted={() => navigator('/enterprise/user')}
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          userId={id!}
          organizationId={organizationId}
        />
      </div>
    )
  }

  if (data) {
    return (
      <div className="flex flex-col gap-4 md:gap-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/enterprise/user">User List</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                {data.firstName} {data.lastName}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1 className="text-2xl md:text-[32px] font-bold">User Profile</h1>
        <Card className="text-charcoal">
          <CardContent className="flex flex-col gap-8 py-8">
            <div className="flex flex-row w-full">
              <h3 className="text-2xl font-bold w-full">Information</h3>
              {!isEditing && <EditButton className="hidden md:flex" />}
            </div>
            {isEditing ? (
              <EditUserForm
                isCurrentUser={isCurrentUser}
                id={id!}
                defaultValues={data}
                onEditing={setIsEditing}
              />
            ) : (
              <div className="flex flex-col gap-6">
                <UserDetailsInformation data={data} />
                <EditButton className="flex md:hidden w-fit" />
              </div>
            )}
            <Separator />
            {!isCurrentUser && (
              <>
                <div className="flex flex-col gap-5">
                  <div className="flex flex-row w-full">
                    <h3 className="text-2xl font-bold w-full ">Status</h3>
                    <StatusButtonAction className="hidden md:flex" />
                  </div>
                  <p>
                    Current status for this user is{' '}
                    <span
                      className={cn('text-primary font-bold', {
                        'text-warning': data.status === UserStatus.Invited,
                        'text-destructive':
                          data.status === UserStatus.Deactivated,
                      })}
                    >
                      {data.status === UserStatus.Active
                        ? 'active'
                        : data.status === UserStatus.Deactivated
                          ? 'deactivated'
                          : 'invited'}
                    </span>
                    .
                  </p>
                  <StatusButtonAction className="flex md:hidden" />
                </div>
                <Separator />
              </>
            )}
            {data.status !== UserStatus.Invited && (
              <>
                <div className="flex flex-col gap-5">
                  <div className="flex flex-row w-full">
                    <h3 className="text-2xl font-bold w-full ">Password</h3>
                    <ResetPasswordButton className="hidden md:flex" />
                  </div>
                  <p>
                    You can reset password for this user by click on the "Reset
                    Password" button.
                  </p>
                  <ResetPasswordButton className="flex md:hidden" />
                </div>
                <Separator />
              </>
            )}

            {!isCurrentUser && (
              <>
                <div className="flex flex-col gap-5">
                  <div className="flex flex-row w-full">
                    <h3 className="text-2xl font-bold w-full ">Delete</h3>
                    <DeleteButton className="hidden md:flex" />
                  </div>
                  <p>To delete a user, click the "Delete" button.</p>
                  <DeleteButton className="flex md:hidden" />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return <FullScreenLoader />
  }
}
